import type { GlBasis } from "@common/schemas/accounting-integration.schema";
import {
    ROLE_LABELS,
    type MigrationRowStatus,
    type MigrationUnverifiedDoc,
} from "./migrationFileMatching.types";

export type MigrationResolutionAction =
    | { type: "select_basis"; basis: GlBasis }
    | { type: "dismiss_parser_warning" };

export interface MigrationResolutionOption {
    label: string;
    action: MigrationResolutionAction;
}

type MigrationResolutionStatus =
    | MigrationRowStatus
    | MigrationUnverifiedDoc["status"];

export function migrationResolutionOptions({
    status,
    isUnknownBasis,
    basisOptions,
}: {
    status: MigrationResolutionStatus;
    isUnknownBasis?: boolean;
    basisOptions: readonly GlBasis[];
}): MigrationResolutionOption[] {
    if (status === "has_warnings") {
        return [
            {
                label: "Proceed anyway",
                action: { type: "dismiss_parser_warning" },
            },
        ];
    }
    if (
        (status === "basis_required" || isUnknownBasis) &&
        basisOptions.length > 0
    ) {
        return basisOptions.map((basis) => ({
            label: ROLE_LABELS[basis],
            action: { type: "select_basis", basis },
        }));
    }
    return [];
}

export function applyMigrationResolutionAction({
    documentId,
    action,
    onSelectBasis,
    onDismissParserWarning,
}: {
    documentId: number;
    action: MigrationResolutionAction;
    onSelectBasis: (documentId: number, basis: GlBasis) => void;
    onDismissParserWarning: (documentId: number) => void;
}): void {
    switch (action.type) {
        case "select_basis":
            onSelectBasis(documentId, action.basis);
            break;
        case "dismiss_parser_warning":
            onDismissParserWarning(documentId);
            break;
    }
}

export function isMigrationActionRow({
    documentId,
    status,
    resolutionOptions,
}: {
    documentId?: number;
    status: MigrationResolutionStatus;
    resolutionOptions: MigrationResolutionOption[];
}): boolean {
    if (documentId == null || status === "extracting") {
        return false;
    }
    return resolutionOptions.length > 0 || status === "unrecognized";
}
